import dayjs from '../../../../shared/services/dayjs.js';
import { stateCls, fuelleZaehler } from '../util/bedarfState.js';
import { eventFarbe } from '../util/eventFarben.js';

/** Kurzformat einer Uhrzeit: volle Stunde ohne Minuten (18), sonst mit (18:30). */
function kurzZeit(datum) {
	const d = dayjs(datum);
	return d.minute() === 0 ? d.format('H') : d.format('H:mm');
}

/**
 * Baut eine Bereich-Gruppe (.bgrp) für eine Tageszelle des Bedarfsplaner-Monatsgrids — den
 * Bereich-Rollup-Chip (.dchip), das "<n> Posten offen"-Label und die eingerückte Posten-Liste (.plist)
 * mit einem .pchip je Bedarfszeile (mehrere Zeitfenster eines Postens = mehrere Chips). Siehe BEDARFSPLANER.md §2.3/§2.4.
 *
 * Posten-Liste UND "<n> Posten offen"-Label werden IMMER ins DOM gerendert; sichtbar ist je nach
 * `.bgrp.collapsed`: zugeklappt zeigt das Label, aufgeklappt die Liste (CSS). Dadurch sind sowohl das
 * zellenweise Ein-/Ausklappen als auch der globale Umschalter reine Klassen-Toggles ohne Re-Render.
 * @param {object} bereichsGruppe {bereichID, bereich, need, filled, zeilen: array} (siehe bedarfState.js:groupiereBedarf)
 * @param {object} optionen
 * @param {boolean} optionen.eingeklappt Posten-Liste dieser Bereichsgruppe in dieser Zelle eingeklappt?
 * @param {function} optionen.onPostenKlick (zeile, bereichsGruppe) => void
 * @param {function} optionen.onToggleCollapse (bereichsGruppe, eingeklappt) => void — Persistieren des Zustands
 * @returns {HTMLElement}
 */
function erstelleBereichsGruppe(bereichsGruppe, { eingeklappt, onPostenKlick, onToggleCollapse }) {

	const bgrp = document.createElement('div');
	bgrp.className = `bgrp${eingeklappt ? ' collapsed' : ''}`;

	const dchip = document.createElement('span');
	dchip.className = `dchip ${stateCls(bereichsGruppe.filled, bereichsGruppe.need)}`;
	dchip.title = bereichsGruppe.zeilen.map((z) => `${z.posten} ${z.zeit} ${z.filled}/${z.need}`).join(' · ');

	// Chevron zeigt den Klappzustand; die Drehung macht das CSS über .bgrp.collapsed (so stimmt er auch nach
	// dem globalen Bereich/Posten-Umschalter, der nur Klassen setzt).
	const chev = document.createElement('i');
	chev.className = 'bi bi-chevron-down chev';
	dchip.appendChild(chev);
	// Die Chip-Farbe kommt aus der Zustandsklasse (full/part/empty), siehe CSS .dchip.full/.part/.empty.
	dchip.appendChild(document.createTextNode(bereichsGruppe.bereich));

	// "<n> Posten offen"-Label: immer gerendert, per CSS nur im Bereich-Level sichtbar (damit im
	// Bereich-Level nie ein nacktes 1/4 ohne Erklärung steht, siehe BEDARFSPLANER.md §1.1/§2.3). Der
	// filled/need-Zähler (.cnt) ist in beiden Leveln sichtbar.
	// Gezählt werden Posten (ein Posten mit zwei offenen Zeitfenstern = 1), passend zur Bezeichnung.
	const offenePosten = new Set(bereichsGruppe.zeilen.filter((z) => z.filled < z.need).map((z) => z.postenID)).size;
	if (offenePosten > 0) {
		const po = document.createElement('span');
		po.className = 'po';
		po.textContent = `${offenePosten} Posten offen`;
		dchip.appendChild(po);
	}
	const cnt = document.createElement('span');
	cnt.className = 'cnt';
	// Besetzung und Kosten, sichtbar je nach Besetzung/Kosten-Umschalter (siehe bedarfState.js:fuelleZaehler)
	fuelleZaehler(cnt, bereichsGruppe.filled, bereichsGruppe.need, bereichsGruppe.geplant);
	dchip.appendChild(cnt);
	// Klick klappt die Posten-Liste dieser Zelle ein/aus (Spoiler) — rein per Klassen-Toggle, ohne
	// Re-Render. Im Bereich-Level ist die Liste ohnehin per CSS ausgeblendet (Klick bleibt folgenlos).
	dchip.addEventListener('click', () => {
		bgrp.classList.toggle('collapsed');
		onToggleCollapse(bereichsGruppe, bgrp.classList.contains('collapsed'));
	});
	bgrp.appendChild(dchip);

	const plist = document.createElement('div');
	plist.className = 'plist';
	bereichsGruppe.zeilen.forEach((zeile) => {
		const pchip = document.createElement('span');
		pchip.className = `pchip ${stateCls(zeile.filled, zeile.need)}`;

		const pn = document.createElement('span');
		pn.className = 'pn';
		pn.textContent = zeile.posten;
		pchip.appendChild(pn);

		const pz = document.createElement('span');
		pz.className = 'pz';
		pz.textContent = zeile.zeit;
		pchip.appendChild(pz);

		const pc = document.createElement('span');
		pc.className = 'pc';
		fuelleZaehler(pc, zeile.filled, zeile.need, zeile.geplant);
		pchip.appendChild(pc);
		pchip.title = `${zeile.filled}/${zeile.need} besetzt`;

		pchip.addEventListener('click', () => onPostenKlick(zeile, bereichsGruppe));
		plist.appendChild(pchip);
	});
	bgrp.appendChild(plist);

	return bgrp;
}

/**
 * In-Zelle-Gruppe für ein mehrtägiges Event: in Event-Farbe getönte Karte mit Kopf (Chevron, Event-Icon,
 * Titel) und den Bedarfszeilen dieses Events an diesem Tag (je Zeile ein .pchip mit Posten · Zeit ·
 * filled/need). Kopf und Farbe ordnen die Gruppe dem Event-Balken zu, auch wenn der in einer anderen
 * Wochenzeile beginnt; beim Hover werden Balken und Gruppen gemeinsam hervorgehoben (monthEventOverlay.js).
 * Klick auf eine Zeile öffnet die Bedarf-Zuordnung, Klick auf den Titel den Event-Editor.
 *
 * Zwei Klappebenen: der Chevron im Balken klappt das ganze Event zu (`.collapsed`, Gruppe komplett weg);
 * der Chevron im Gruppenkopf klappt nur DIESEN Tag zu (`.tag-zu`, Kopf bleibt, Zeilen weg) — rein per
 * Klassen-Toggle, die Drehung des Chevrons macht das CSS.
 * @param {object} event
 * @param {array<object>} rows Bedarfszeilen des Events an diesem Tag (mit filled)
 * @param {object} optionen
 * @param {boolean} optionen.eingeklappt ganzes Event zugeklappt?
 * @param {boolean} optionen.tagEingeklappt nur diese Tagesgruppe zugeklappt?
 * @param {function} optionen.onPostenKlick (bedarfID) => void
 * @param {function} optionen.onEventKlick (event) => void
 * @param {function} optionen.onTagToggle (eingeklappt) => void — Zustand der Tagesgruppe merken
 * @returns {HTMLElement}
 */
function erstelleEventGruppe(event, rows, { eingeklappt, tagEingeklappt, onPostenKlick, onEventKlick, onTagToggle }) {
	const grp = document.createElement('div');
	grp.className = `evgrp${eingeklappt ? ' collapsed' : ''}${tagEingeklappt ? ' tag-zu' : ''}`;
	grp.dataset.eventId = event._id;
	grp.style.setProperty('--evf', eventFarbe(event));

	const kopf = document.createElement('div');
	kopf.className = 'evgrp-kopf';
	const tagToggle = document.createElement('span');
	tagToggle.className = 'evtoggle tagtoggle';
	tagToggle.title = 'Schichten dieses Tages ein-/ausblenden';
	tagToggle.innerHTML = '<i class="bi bi-chevron-down"></i>';
	tagToggle.addEventListener('click', (e) => {
		e.stopPropagation();
		grp.classList.toggle('tag-zu');
		onTagToggle(grp.classList.contains('tag-zu'));
	});
	kopf.appendChild(tagToggle);
	const titel = document.createElement('span');
	titel.className = 'evgrp-titel';
	titel.title = event.Titel || '';
	titel.innerHTML = `<i class="bi bi-${event.Icon || 'stars'}"></i>`;
	const ttx = document.createElement('span');
	ttx.className = 'ttx';
	ttx.textContent = event.Titel || '';
	titel.appendChild(ttx);
	titel.addEventListener('click', () => onEventKlick(event));
	kopf.appendChild(titel);
	grp.appendChild(kopf);

	const zeilen = document.createElement('div');
	zeilen.className = 'evgrp-zeilen';
	grp.appendChild(zeilen);

	rows.forEach((r) => {
		const pchip = document.createElement('span');
		pchip.className = `pchip ${stateCls(r.filled || 0, r.BedarfAnzahl)}`;

		const pn = document.createElement('span');
		pn.className = 'pn';
		pn.textContent = r.Posten || '';
		// Bereich als kleiner Zusatz wie in den eintägigen Event-Karten (Event-Zeilen mischen Bereiche).
		if (r.Bereich) {
			const bereich = document.createElement('small');
			bereich.textContent = r.Bereich;
			pn.appendChild(bereich);
		}
		pchip.appendChild(pn);

		const pz = document.createElement('span');
		pz.className = 'pz';
		pz.textContent = `${kurzZeit(r.SchichtVon)}-${kurzZeit(r.SchichtBis)}`;
		pchip.appendChild(pz);

		const pc = document.createElement('span');
		pc.className = 'pc';
		fuelleZaehler(pc, r.filled || 0, r.BedarfAnzahl, r.geplant);
		pchip.appendChild(pc);
		pchip.title = `${r.filled || 0}/${r.BedarfAnzahl} besetzt`;

		pchip.addEventListener('click', () => onPostenKlick(r._id));
		zeilen.appendChild(pchip);
	});
	return grp;
}

export { erstelleBereichsGruppe, erstelleEventGruppe, kurzZeit };
